import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AppLayout } from "@/components/AppLayout";
import { useI18n } from "@/i18n/I18nContext";
import { SessionCard, fmtDate, fmtTime } from "@/components/SessionCard";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Ticket, CalendarClock, AlertTriangle, Bell, ArrowRight, Flame, Dumbbell } from "lucide-react";

export function Dashboard() {
  const { t, tl, lang } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [wod, setWod] = useState(null);
  const [streak, setStreak] = useState(null);
  const [sanction, setSanction] = useState(null);

  useEffect(() => {
    api.get("/me/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.get("/me/notifications").then(({ data }) => setNotifs(data.slice(0, 4))).catch(() => {});
    api.get("/workouts/today").then(({ data }) => setWod(data)).catch(() => {});
    api.get("/me/streak").then(({ data }) => setStreak(data)).catch(() => {});
    api.get("/me/sanction").then(({ data }) => setSanction(data)).catch(() => {});
  }, []);

  if (!data) return <AppLayout><div className="py-20 text-center text-muted-foreground">{t("common.loading")}</div></AppLayout>;

  const u = data.user;
  const nb = data.next_booking;
  const ACTION_LABEL = { health: t("dashboard.completeHealth"), payment_pending: t("dashboard.paymentPending"), pack_expiring: t("dashboard.packExpiring"), pack_low: t("dashboard.packLow") };

  return (
    <AppLayout>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mt-2 flex items-center gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("dashboard.welcome")}, {u.first_name} 👋</h1>
        </div>
        {streak && streak.streak > 0 && (
          <div data-testid="streak-badge" className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
            <Flame className="h-4 w-4" /> {streak.streak} {tl({ es: "días seguidos", en: "day streak", fr: "jours d'affilée" })}
            {streak.trained_today && <span className="text-xs font-normal text-muted-foreground">· {tl({ es: "¡hoy hecho!", en: "done today!", fr: "fait aujourd'hui !" })}</span>}
          </div>
        )}

        {sanction?.active && (
          <div data-testid="sanction-banner" className="mt-5 flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3.5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/20">
              <Dumbbell className="h-6 w-6 text-amber-500" />
            </div>
            <div className="min-w-0 text-sm">
              <div className="font-display text-base font-bold text-amber-500">{tl({ es: "¡Reto de asistencia!", en: "Attendance challenge!", fr: "Défi assiduité !" })}</div>
              <div className="text-muted-foreground">
                {tl({
                  es: `${sanction.count} ausencias en 2 semanas → ${sanction.burpees} burpees 😜 ¡Nos vemos en la próxima clase!`,
                  en: `${sanction.count} absences in 2 weeks → ${sanction.burpees} burpees 😜 See you at the next class!`,
                  fr: `${sanction.count} absences en 2 semaines → ${sanction.burpees} burpees 😜 On t'attend au prochain cours !`,
                })}
              </div>
            </div>
          </div>
        )}

        {data.action_items?.length > 0 && (
          <div className="mt-5 space-y-2">
            {data.action_items.map((a) => (
              <button key={a.type} data-testid={`action-item-${a.type}`} onClick={() => navigate(a.type === "health" ? "/app/profile" : a.type === "payment_pending" ? "/app/credits" : "/app/credits")}
                className="flex w-full items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-left text-sm">
                <AlertTriangle className="h-5 w-5 text-primary" /> {ACTION_LABEL[a.type]}
                <ArrowRight className="ml-auto h-4 w-4" />
              </button>
            ))}
          </div>
        )}

        {wod && (
          <button
            data-testid="dashboard-wod-card" onClick={() => navigate("/app/wod")}
            className="mt-6 flex w-full items-center gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-6 text-left transition-colors hover:bg-primary/10"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15">
              <Flame className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary">{tl({ es: "Rutina del día", en: "Workout of the day", fr: "Séance du jour" })}</div>
              <div className="truncate font-display text-lg font-semibold">{wod.title}</div>
              {wod.my_result && Object.values(wod.my_result.values || {}).some((v) => v)
                ? <div className="mt-0.5 text-xs text-muted-foreground">{tl({ es: "Resultado registrado", en: "Result logged", fr: "Résultat enregistré" })} ✓</div>
                : <div className="mt-0.5 text-xs text-muted-foreground">{(wod.blocks || []).length} {tl({ es: "bloques · Registra tu resultado", en: "blocks · Log your result", fr: "blocs · Enregistre ton résultat" })}</div>}
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground" />
          </button>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><CalendarClock className="h-4 w-4" /> {t("dashboard.nextBooking")}</div>
            {nb ? (
              <div className="mt-3">
                <div className="font-display text-xl font-semibold">{tl(nb.class_type?.name)}</div>
                <div className="mt-1 text-sm text-muted-foreground">{fmtDate(nb.start, lang)} · {fmtTime(nb.start, lang)} · {nb.coach?.name}</div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">{t("dashboard.noBooking")}</p>
            )}
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Ticket className="h-4 w-4" /> {t("dashboard.availableCredits")}</div>
            <div className="mt-3 font-display text-4xl font-bold">{data.credits_remaining}</div>
            {data.next_expiry && <p className="mt-1 text-xs text-muted-foreground">{t("dashboard.packExpires")}: {fmtDate(data.next_expiry, lang)}</p>}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button size="lg" data-testid="dashboard-book-btn" onClick={() => navigate("/app/schedule")} className="rounded-full">{t("dashboard.quickBook")} <ArrowRight className="ml-2 h-4 w-4" /></Button>
          <Button size="lg" variant="outline" data-testid="dashboard-credits-btn" onClick={() => navigate("/app/credits")} className="rounded-full">{t("nav.credits")}</Button>
        </div>

        {notifs.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold"><Bell className="h-5 w-5" /> {t("dashboard.actionsNeeded")}</h2>
            <div className="space-y-2">
              {notifs.map((n) => (
                <div key={n.notification_id} data-testid={`notif-${n.notification_id}`} className="rounded-xl border border-border bg-card px-4 py-3 text-sm">
                  <div className="font-medium">{n.title}</div>
                  <div className="text-muted-foreground">{n.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8">
          <h2 className="mb-3 font-display text-lg font-semibold">{t("dashboard.upcoming")}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {data.upcoming_sessions?.slice(0, 4).map((s) => <SessionCard key={s.session_id} session={s} compact onBook={() => navigate("/app/schedule")} onWaitlist={() => navigate("/app/schedule")} />)}
          </div>
        </div>
      </motion.div>
    </AppLayout>
  );
}
